import { obtenerMensajeError } from "utils/apiErrors";
import React, { useState, useEffect } from "react";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import {
  Box,
  Paper,
  IconButton,
  MenuItem,
  TextField,
  InputAdornment,
  Menu,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import AppsIcon from "@mui/icons-material/Apps";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";

import SearchIcon from "@mui/icons-material/Search";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";

import TableTopControls from "common/TableTopControls";

import UsuariosApi from "api/userApi";
import EditUsuarioModal from "../components/editUsuarioModal";

const UsuariosTable = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedUsuario, setSelectedUsuario] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const handleMenuClick = (event, id) => {
    setAnchorEl(event.currentTarget);
    setSelectedId(id);
    let usuario = usuarios.find((a) => a.id === id);
    setSelectedUsuario(usuario);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedId(null);
  };

  const handleEdit = (usuario) => {
    setSelectedUsuario(usuario);
    setEditModalOpen(true);
    handleMenuClose();
  };

  const fetchUsuarios = async () => {
    try {
      const response = await UsuariosApi.fetchUsuarios();
      setUsuarios(response.data);
    } catch (error) {
      console.error("Error al obtener los usuarios:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsuarios();
  }, []);

  const handleToggleActive = async (user) => {
    const confirmAction = window.confirm(
      `¿Estás seguro(a) que deseas ${user.is_active ? "desactivar" : "activar"} este usuario?`
    );
    if (confirmAction) {
      try {
        const response = await UsuariosApi.toggleActive(user.id);
        if (response.status === 200) {
          alert("Estado de usuario actualizado correctamente");
          fetchUsuarios(); // Recargar la lista de usuarios para reflejar el cambio
        }
      } catch (error) {
        alert(obtenerMensajeError(error, "Error al actualizar el estado"));
      }
    }
    handleMenuClose();
  };

  const filteredUsuarios = usuarios.filter((usuario) => {
    const matchesSearch =
      usuario.nombre_completo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(usuario.identificacion).toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const paginatedUsuarios = filteredUsuarios.slice(page * pageSize, (page + 1) * pageSize);

  const columns = [
    {
      field: "actions",
      renderHeader: () => <AppsIcon />,
      width: 50,
      sortable: false,
      align: "left",
      disableColumnMenu: true,
      resizable: false,
      renderCell: (params) => (
        <IconButton onClick={(e) => handleMenuClick(e, params.row.id)}>
          <MoreVertIcon />
        </IconButton>
      ),
    },
    {
      field: "identificacion",
      headerName: "Identificación",
      flex: 2,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.identificacion}
        </SuiTypography>
      ),
    },
    {
      field: "nombre_completo",
      headerName: "Nombre",
      flex: 2,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.nombre_completo}
        </SuiTypography>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      flex: 2,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.email || "No tiene."}
        </SuiTypography>
      ),
    },
    {
      field: "is_active",
      headerName: "Estado",
      flex: 1,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {params.value ? (
            // Si está activo, mostrar chulito gris
            <CheckCircleIcon color="success" fontSize="small" />
          ) : (
            // Si está inactivo, mostrar X roja
            <CancelIcon color="error" fontSize="small" />
          )}
        </Box>
      ),
    },

    {
      field: "groups_det",
      headerName: "Roles",
      flex: 2,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.groups_det && params.row.groups_det.length > 0
            ? params.row.groups_det
                .filter((grupo) => grupo.name !== "INVITADO") // Excluir "INVITADO"
                .map((grupo) => grupo.name)
                .join(", ") || "No tiene." // Mostrar "No tiene." si no hay roles válidos
            : "No tiene."}
        </SuiTypography>
      ),
    },
  ];

  return (
    <SuiBox>
      <Paper
        sx={{
          width: "100%",
          overflow: "hidden",
          boxShadow: "none",
          backgroundColor: "transparent",
        }}
      >
        <SuiBox
          justifyContent="space-between"
          alignItems="center"
          p={1}
          sx={{
            borderBottom: ({ borders: { borderWidth, borderColor } }) =>
              `${borderWidth[1]} solid ${borderColor}`,
          }}
        >
          <Grid container columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={6}>
              <SuiBox display="flex" flexDirection="column">
                <strong>Administración de Usuarios</strong>
              </SuiBox>
            </Grid>
          </Grid>
          <SuiBox display="flex" alignItems="center" gap={2}>
            <Tooltip title="Buscar por Nombre y Dirección" arrow>
              <TextField
                size="small"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(0);
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Tooltip>
            <TableTopControls
              page={page}
              onPageChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              totalRows={filteredUsuarios.length}
            />
          </SuiBox>
        </SuiBox>

        <DataGrid
          rows={paginatedUsuarios}
          columns={columns}
          //disableSelectionOnClick
          loading={loading}
          getRowHeight={() => "auto"}
          sx={{
            border: 0,
            backgroundColor: "white",
            borderRadius: 2,
            p: 2,
            [`& .${gridClasses.cell}`]: {
              borderBottom: "none",
              padding: "2px 2px",
            },
            [`& .${gridClasses.row}`]: {
              backgroundColor: ({ palette: { white } }) => white.main,
              "&:hover": {
                backgroundColor: ({ palette: { grey } }) => grey[100],
              },
              minHeight: "48px !important",
            },
            [`& .${gridClasses.columnHeaders}`]: {
              backgroundColor: ({ palette: { grey } }) => grey[50],
              borderRadius: 1,
              minHeight: "48px !important",
            },
            [`& .${gridClasses.footerContainer}`]: {
              display: "none",
            },
          }}
          hideFooter
        />
      </Paper>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={() => handleEdit(usuarios.find((a) => a.id === selectedId))}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Editar
        </MenuItem>
        <MenuItem onClick={() => handleToggleActive(usuarios.find((a) => a.id === selectedId))}>
          {selectedUsuario?.is_active ? (
            <>
              <CancelIcon color="error" fontSize="small" sx={{ mr: 1 }} />
              Desactivar
            </>
          ) : (
            <>
              <CheckCircleIcon color="success" sx={{ mr: 1 }} fontSize="small" />
              Activar
            </>
          )}
        </MenuItem>
      </Menu>

      <EditUsuarioModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        usuario={selectedUsuario}
        onSuccess={() => {
          fetchUsuarios();
          setEditModalOpen(false);
        }}
      />
    </SuiBox>
  );
};

export default UsuariosTable;
